import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { demoMode } from '../lib/demoMode';

const ALARM_QUERY_KEYS = ['alarms', 'alarm-feed', 'plant-scene'];
const MIN_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/** The stream route sits next to the REST routes: ws(s)://<api host>/api/v1/alarms/stream. */
function streamUrl(): string {
  const url = new URL(`${import.meta.env.VITE_API_URL}/alarms/stream`, window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/**
 * Keeps the alarm views live: the backend pushes a nudge over a WebSocket whenever an alarm is
 * raised, resolved or acknowledged, and this refetches the affected queries. The REST polling in
 * useAlarms/useAlarmFeed stays as a slow fallback for when the socket is down.
 *
 * The access token is sent as the first message rather than in the URL, so it never lands in logs.
 */
export function useAlarmStream(): void {
  const queryClient = useQueryClient();
  const authenticated = useAuthStore((s) => s.status === 'authenticated');

  useEffect(() => {
    if (!authenticated || demoMode.active) return;

    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let retryMs = MIN_RETRY_MS;
    let stopped = false;

    const connect = () => {
      const token = useAuthStore.getState().accessToken;
      if (!token) return;

      socket = new WebSocket(streamUrl());

      socket.onopen = () => {
        socket?.send(JSON.stringify({ type: 'auth', token }));
      };

      socket.onmessage = () => {
        retryMs = MIN_RETRY_MS; // เซิร์ฟเวอร์จะตอบกลับเมื่อยอมรับ token แล้วเท่านั้น
        for (const key of ALARM_QUERY_KEYS) {
          void queryClient.invalidateQueries({ queryKey: [key] });
        }
      };

      socket.onerror = () => {

      };

      socket.onclose = () => {
        if (stopped) return;
        retryTimer = window.setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
      };
    };

    connect();

    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);

      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;

        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
      }
    };
  }, [authenticated, queryClient]);
}
